import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import type { PathTraceData, Waypoint } from "./types";

const { fontFamily } = loadFont("normal", { weights: ["600", "700"], subsets: ["latin"] });

const GOLD = "#D4A853";
const W = 1280, H = 704;

function pathLength(pts: { x: number; y: number }[]): number {
  return pts.slice(1).reduce((sum, p, i) => sum + Math.hypot(p.x - pts[i].x, p.y - pts[i].y), 0);
}

export const PathTrace: React.FC<{ data: PathTraceData; durationInFrames: number }> = ({ data, durationInFrames }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const pts = data.waypoints.map((wp: Waypoint) => ({ x: wp.x * W, y: wp.y * H, label: wp.label }));
  const total = pathLength(pts);

  const traceProgress = interpolate(frame, [0, Math.round(1.8 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.25, 0.46, 0.45, 0.94),
  });
  const labelOpacity = interpolate(frame, [Math.round(1.6 * fps), Math.round(2.2 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.6 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  // Build polyline path
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");

  // Trace animation via stroke-dasharray trick
  const dashOffset = total * (1 - traceProgress);

  // Per-waypoint dot opacity: show when trace reaches that waypoint
  let cumLen = 0;
  const dotOpacities = pts.map((p, i) => {
    if (i === 0) return traceProgress > 0 ? 1 : 0;
    cumLen += Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y);
    const threshold = cumLen / total;
    return interpolate(traceProgress, [threshold - 0.02, threshold + 0.04], [0, 1], {
      extrapolateRight: "clamp", extrapolateLeft: "clamp",
    });
  });

  return (
    <AbsoluteFill style={{ opacity: fadeOut, pointerEvents: "none" }}>
      <svg width={W} height={H} style={{ position: "absolute", top: 0, left: 0 }}>
        {/* Glow shadow path */}
        <path
          d={pathD} fill="none"
          stroke="rgba(212,168,83,0.25)" strokeWidth={8} strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={total} strokeDashoffset={dashOffset}
        />
        {/* Main traced path */}
        <path
          d={pathD} fill="none"
          stroke={GOLD} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={total} strokeDashoffset={dashOffset}
          opacity={0.9}
        />

        {/* Waypoint dots and labels */}
        {pts.map((p, i) => (
          <g key={i} opacity={dotOpacities[i]}>
            {/* Outer ring */}
            <circle cx={p.x} cy={p.y} r={9} fill="rgba(212,168,83,0.25)" stroke={GOLD} strokeWidth={1.5} />
            {/* Inner dot */}
            <circle cx={p.x} cy={p.y} r={4} fill={GOLD} />

            {/* Waypoint label */}
            {p.label && (
              <g opacity={labelOpacity}>
                <rect
                  x={p.x + 14} y={p.y - 16}
                  width={p.label.length * 7.5 + 16} height={30}
                  rx={6}
                  fill="rgba(0,0,0,0.78)"
                  stroke={GOLD} strokeOpacity={0.35} strokeWidth={1}
                />
                <text
                  x={p.x + 22} y={p.y + 1}
                  dominantBaseline="middle"
                  fill="rgba(255,255,255,0.9)" fontSize={13} fontWeight={600} fontFamily={fontFamily}
                >
                  {p.label}
                </text>
              </g>
            )}
          </g>
        ))}

        {/* Route label at start */}
        <g opacity={labelOpacity}>
          <rect
            x={pts[0].x - 12} y={pts[0].y - 46}
            width={data.label.length * 9 + 20} height={30}
            rx={6}
            fill="rgba(0,0,0,0.82)"
            stroke={GOLD} strokeOpacity={0.5} strokeWidth={1.5}
          />
          <text
            x={pts[0].x - 3} y={pts[0].y - 29}
            dominantBaseline="middle"
            fill={GOLD} fontSize={14} fontWeight={700} fontFamily={fontFamily}
          >
            {data.label}
          </text>
        </g>
      </svg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
